import { useEffect, useState, type ReactNode } from "react";
import {
  motion,
  useReducedMotion,
  useTime,
  useTransform,
  type MotionValue,
} from "framer-motion";

/**
 * Decorative band of fake contact cards beneath the hero CTAs. The cards scroll
 * horizontally and ride a shallow arc — rising over a gentle hump as they cross
 * the center, tilting with the arc's tangent — then loop seamlessly off-screen
 * at each edge. A calm "contacts streaming in" motif.
 *
 * Purely decorative: `aria-hidden` + `pointer-events-none`. Honors
 * `prefers-reduced-motion` by laying the cards out along the arc statically
 * (Framer Motion's transforms aren't covered by the global CSS reduce rule).
 */

const CONTACTS = [
  { initials: "AL", name: "Amélie Laurent", address: "14 Rue des Lilas" },
  { initials: "TF", name: "Théo Fontaine", address: "8 Maple Court" },
  { initials: "MW", name: "Mara Whitfield", address: "221 Birch Lane" },
  { initials: "JR", name: "Julian Reyes", address: "5 Harbor View" },
  { initials: "PN", name: "Priya Nair", address: "92 Elm Street" },
  { initials: "SD", name: "Søren Dahl", address: "3 Nyhavn Plads" },
];

const COUNT = CONTACTS.length;
/** Milliseconds for one card to travel the full arc, edge to edge. */
const PERIOD = 24000;
/** Peak rise of the hump, and max tangent tilt at the edges. */
const ARC_HEIGHT = 60;
const ARC_TILT = 8;

/**
 * Horizontal travel, as % of the band, from off-screen left to off-screen
 * right. A wider span spreads the cards further apart, so on mobile — where the
 * band is narrow and the cards nearly fill it — we stretch the track so only
 * one or two cards share the band at a time instead of overlapping.
 */
const SPREAD = {
  mobile: { start: -190, span: 480 },
  desktop: { start: -25, span: 150 },
};

/** Position along the arc for a phase in [0, 1). */
function arcAt(phase: number, start: number, span: number) {
  return {
    left: `${start + phase * span}%`,
    y: -ARC_HEIGHT * Math.sin(Math.PI * phase),
    rotate: -ARC_TILT * Math.cos(Math.PI * phase),
  };
}

/** True at the `md` breakpoint and up; defaults to desktop when unknown. */
function useIsDesktop() {
  const [desktop, setDesktop] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return desktop;
}

const CARD_CLASS =
  "absolute top-16 flex w-56 items-center gap-4 rounded-xl glass-surface px-5 py-4 text-left shadow-md";

function CardBody({ contact }: { contact: (typeof CONTACTS)[number] }): ReactNode {
  return (
    <>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-muted type-label-base text-brand-strong">
        {contact.initials}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="type-label-base truncate text-text">{contact.name}</span>
        <span className="type-body-sm truncate text-text-muted">
          {contact.address}
        </span>
      </span>
    </>
  );
}

function ArcCard({
  time,
  index,
  contact,
  start,
  span,
}: {
  time: MotionValue<number>;
  index: number;
  contact: (typeof CONTACTS)[number];
  start: number;
  span: number;
}) {
  const phase = (ms: number) => (((ms / PERIOD + index / COUNT) % 1) + 1) % 1;
  const left = useTransform(time, (ms) => arcAt(phase(ms), start, span).left);
  const y = useTransform(time, (ms) => arcAt(phase(ms), start, span).y);
  const rotate = useTransform(time, (ms) => arcAt(phase(ms), start, span).rotate);

  return (
    <motion.div className={CARD_CLASS} style={{ left, y, rotate, translateX: "-50%" }}>
      <CardBody contact={contact} />
    </motion.div>
  );
}

function AnimatedArc({ start, span }: { start: number; span: number }) {
  const time = useTime();
  return (
    <>
      {CONTACTS.map((contact, index) => (
        <ArcCard
          key={contact.address}
          time={time}
          index={index}
          contact={contact}
          start={start}
          span={span}
        />
      ))}
    </>
  );
}

function StaticArc({ start, span }: { start: number; span: number }) {
  return (
    <>
      {CONTACTS.map((contact, index) => {
        const { left, y, rotate } = arcAt(index / COUNT, start, span);
        return (
          <div
            key={contact.address}
            className={CARD_CLASS}
            style={{
              left,
              transform: `translateX(-50%) translateY(${y}px) rotate(${rotate}deg)`,
            }}
          >
            <CardBody contact={contact} />
          </div>
        );
      })}
    </>
  );
}

export function HeroContactCards() {
  const reduced = useReducedMotion();
  const desktop = useIsDesktop();
  const { start, span } = desktop ? SPREAD.desktop : SPREAD.mobile;

  return (
    <div
      aria-hidden
      className="pointer-events-none relative mx-auto h-52 w-full overflow-hidden"
      style={{
        maskImage:
          "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
      }}
    >
      {reduced ? (
        <StaticArc start={start} span={span} />
      ) : (
        <AnimatedArc start={start} span={span} />
      )}
    </div>
  );
}
